import React from 'react';
import Component from '../component/Component';
import { Position } from '../../Types';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';
import _ from 'lodash';
import { error } from '../../../utility/Util';
import { CUSTOM_TAG_PROPS } from './Types';

class CustomTag extends Component<CUSTOM_TAG_PROPS, {}> {
  private getBodyType = () => {
    let renderContent = null;
    switch (this.props.type) {
      case 'label':
        renderContent = this.getLabel();
        break;
      case 'counter':
        renderContent = this.getCounter();
        break;
      case 'buttonCounter':
        renderContent = this.getButtonCounter();
        break;
      case 'notificationLabelWithCounter':
        renderContent = this.getNotificationLabelWithCounter();
        break;
      case 'notificationLabel':
        renderContent = this.getNotificationLabel();
        break;
      case 'buttonGroup':
        renderContent = this.buttonGroup();
        break;
      case 'switcher':
        renderContent = this.switcher();
        break;
      case 'closeIcon':
        renderContent = this.getCloseIcon();
        break;
      default:
        break;
    }
    if (!_.isUndefined(this.props.disabled) && !['buttonCounter', 'buttonGroup', 'switcher'].includes(this.props.type)) {
      error(`"disabled" property is not supported for this custom tag type: ${this.props.type}`, 'warn');
    }
    return (
      renderContent
    );
  };

  /**
   * Renders plain text inside dialog footer
   */
  private getCloseIcon = () => {
    if (this.props.type === 'closeIcon') {
      const [index, closeDialog, ariaLabelTitle] = this.props.args;
      return (
        <a
          href="#"
          role="button"
          aria-label={ariaLabelTitle}
          onClick={(e) => {
            closeDialog(e, index);
          }}
          className="k-button k-button-flat k-button-flat-base k-button-icon k-window-action k-dialog-action k-dialog-close"
        >
          <i aria-hidden="true" className="k-icon k-i-x" />
        </a>
      );
    }
  };

  /**
   * Renders plain text inside dialog footer
   */
  private getLabel = () => {
    if (this.props.type === 'label') {
      const [text, additionalText, asSubmissionName] = this.props.args;

      let labelCls = 'tp-text-label k-text-ellipsis';
      if (asSubmissionName) {
        labelCls += ' tp-submission-name';
      }
      let additional = additionalText ? (
        <>
          <span>|</span>
          <span>{this.translate(additionalText)}</span>
        </>
      ) : null;
      return (
        <span
          className={labelCls}
          // <debug>
          data-cy={'customTag_label'}
        // </debug>
        >
          <span>{this.translate(text)}</span>
          {additional}
        </span>
      );
    }
  };

  /**
   * Renders label with counter. If translation is missing then only counter value will be returned.
   */
  private getCounter = () => {
    if (this.props.type === 'counter') {
      const [text, value] = this.props.args;
      const translated = this.translate(text, { counter: value });

      return (
        <span
          role="alert"
          aria-live="polite"
          className={'tp-selected-files-counter'}
          // <debug>
          data-cy={'customTag_counter'}
          // </debug>
        >
          {text !== translated ? translated : value}
        </span>
      );
    }
  }
  /**
   * Renders label with button counter. If translation is missing then only counter value will be returned.
   */
  private getButtonCounter = () => {
    if (this.props.type === 'buttonCounter') {
      const [text, value, action, primary] = this.props.args;
      const translated = this.translate(text, { counter: value });

      return (
        <span aria-live={'polite'}>
          <Button
            disabled={this.props.disabled}
            className={'tp-button-counter'}
            themeColor={primary ? 'primary' : null}
            onClick={() => {
              this.dispatch(action, Position.end);
            }}
            // <debug>
            data-cy={'customTag_buttonCounter'}
            // </debug>
          >
            {text !== translated ? translated : value}
          </Button>
        </span>
      );
    }
  };

  /**
   * Renders notification label with counter inside dialog footer
   */
  private getNotificationLabelWithCounter = () => {
    if (this.props.type === 'notificationLabelWithCounter') {
      const [counterText, counterNumber, notificationText, notificationVariant, notificationIcon] = this.props.args;
      const translated = this.translate(counterText, { 
        counter: counterNumber 
      });
      let counterCls = 'tp-selected-files-counter k-text-right';
      let variantCls = !!notificationVariant ? `tp-notification-bar tp-${notificationVariant}` : 'tp-notification-bar';
      let iconCls = !!notificationIcon ? `tp-icon tp-${notificationIcon}` : '';

      return (
        <div
          className={'tp-dialog-footer-notification-with-counter'}
          // <debug>
          data-cy={'customTag_notificationLabelWithCounter'}
          // </debug>
        >
          <span className={counterCls}>{translated}</span>
          <div className={variantCls}>
            <span className={iconCls} />
            {this.translate(notificationText)}
          </div>
        </div>
      );
    }
  };

  /**
   * Renders notification label inside dialog footer
   */
  private getNotificationLabel = () => {
    if (this.props.type === 'notificationLabel') {
      const [text, variant, icon] = this.props.args;
      let variantCls = !!variant ? `tp-notification-bar tp-${variant}` : 'tp-notification-bar';
      let iconCls = !!icon ? `tp-icon tp-${icon}` : '';

      return (
        <div
          className={variantCls}
          // <debug>
          data-cy={'customTag_notificationLabel'}
          // </debug>
        >
          <span className={iconCls} />
          {this.translate(text)}
        </div>
      );
    }
  };

  /**
   * Renders buttonGroup as custom tag for dialog header or footer
   */
  private buttonGroup = () => {
    if (this.props.type === 'buttonGroup') {
      const [firstButtonText, firstBtnAction, secondButtonText, secondButtonAction] = this.props.args;
      let { activeBtn = 1 } = this.state;

      return (
        <ButtonGroup>
          <Button
            disabled={this.props.disabled}
            themeColor={activeBtn === 1 ? 'primary' : null}
            onClick={() => {
              this.setState({ activeBtn: 1 });
              this.dispatch(firstBtnAction, Position.end);
            }}
            // <debug>
            data-cy={'customTag_buttonGroup_1'}
            // </debug>
          >
            {this.translate(firstButtonText)}
          </Button>
          <Button
            disabled={this.props.disabled}
            themeColor={activeBtn === 2 ? 'primary' : null}
            onClick={() => {
              this.setState({ activeBtn: 2 });
              this.dispatch(secondButtonAction, Position.end);
            }}
            // <debug>
            data-cy={'customTag_buttonGroup_2'}
            // </debug>
          >
            {this.translate(secondButtonText)}
          </Button>
        </ButtonGroup>
      );
    }
  };

  /**
   * Renders switcher as custom tag for dialog header or footer
   */
  private switcher = () => {
    if (this.props.type === 'switcher') {
      const [firstLabel, firstAction, secondLabel, secondAction] = this.props.args;
      let { checked = !firstAction } = this.state;

      const firstLabelActiveCls = !checked ? 'tp-active' : '';
      const secondLabelActiveCls = checked ? 'tp-active' : '';

      return (
        <div className={'tp-dialog-header-switcher'}>
          <span
            className={firstLabelActiveCls}
          >
            {this.translate(firstLabel)}
          </span>
          <Switch
            disabled={this.props.disabled}
            onLabel={this.translate(secondLabel)}
            offLabel={this.translate(firstLabel)}
            checked={checked}
            onChange={(e) => {
              this.dispatch(checked ? firstAction : secondAction, Position.end);
              this.setState({ checked: !checked });
            }}
            // <debug>
            data-cy={'customTag_switcher'}
            // </debug>
          />
          <span
            className={secondLabelActiveCls}
          >
            {this.translate(secondLabel)}
          </span>
        </div>
      );
    }
  };

  renderBody(): any {
    return this.getBodyType();
  }
}

export default CustomTag;
